import axios from 'axios'

const LIST_NDVI_REPORTS_PATH = '/api/v2/fields/lands/reports/ndvi'
const NEW_NDVI_REPORTS_PATH = '/api/v2/fields/lands/reports/ndvi'
const GET_NDVI_REPORTS_PATH = '/api/v2/fields/lands/reports/ndvi/:id'
const LIST_TECHNICAL_REPORTS_PATH = '/api/v2/fields/lands/reports/technical'
const NEW_TECHNICAL_REPORTS_PATH = '/api/v2/fields/lands/reports/technical'
const GET_TECHNICAL_REPORTS_PATH = '/api/v2/fields/lands/reports/technical/:id'
const TECHNICAL_REPORT_ARTIFACT_PATH =
  '/api/v2/fields/lands/reports/technical/:id/artifacts/:artifact'
const LIST_ECONOMIC_REPORTS_PATH = '/api/v2/fields/lands/reports/economic-by-environment'
const NEW_ECONOMIC_REPORTS_PATH = '/api/v2/fields/lands/reports/economic-by-environment'
const GET_ECONOMIC_REPORTS_PATH = '/api/v2/fields/lands/reports/economic-by-environment/:id'
const INSPECT_ECONOMIC_REPORT_UPLOAD_PATH =
  '/api/v2/fields/lands/reports/economic-by-environment/uploads/inspect'
const ECONOMIC_REPORT_GEOJSON_PATH = '/api/v2/geojson'
const ECONOMIC_REPORT_ARTIFACT_PATH_PREFIX = '/api/v2/fields/lands/reports/economic-by-environment/'
const TECHNICAL_REPORT_ARTIFACT_PATH_PREFIX = '/api/v2/fields/lands/reports/technical/'

const normalizeArtifactPath = (downloadPath, expectedPrefix) => {
  if (typeof downloadPath !== 'string') {
    return null
  }

  const trimmedPath = downloadPath.trim()
  if (!trimmedPath) {
    return null
  }

  try {
    const origin = window.location.origin
    const url = new URL(trimmedPath, origin)

    if (url.origin !== origin) {
      return null
    }

    const path = `${url.pathname}${url.search}`
    return path.startsWith(expectedPrefix) ? path : null
  } catch (error) {
    return null
  }
}

const normalizeEconomicReportArtifactPath = (downloadPath) =>
  normalizeArtifactPath(downloadPath, ECONOMIC_REPORT_ARTIFACT_PATH_PREFIX)

const normalizeTechnicalReportArtifactPath = (downloadPath) =>
  normalizeArtifactPath(downloadPath, TECHNICAL_REPORT_ARTIFACT_PATH_PREFIX)

export function list(page, size) {
  return axios
    .get(LIST_NDVI_REPORTS_PATH, {
      params: {page, size},
    })
    .then((res) => {
      if (res.status === 200) {
        return {
          ok: true,
          data: res.data,
        }
      }
    })
}

export function newNdviReport(data) {
  return axios.post(NEW_NDVI_REPORTS_PATH, data).then((res) => {
    if (res.status === 202) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function getNdviReport(id) {
  return axios.get(GET_NDVI_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function deleteNdviReport(id) {
  return axios.delete(GET_NDVI_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200 || res.status === 204) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

const normalizeEconomicReportList = (data = {}) => ({
  ...data,
  economic_reports: data.economic_reports || data.economicReports || [],
})

export function listEconomicReports(page, size) {
  return axios
    .get(LIST_ECONOMIC_REPORTS_PATH, {
      params: {page, size},
    })
    .then((res) => {
      if (res.status === 200) {
        return {
          ok: true,
          data: normalizeEconomicReportList(res.data),
        }
      }
    })
}

export function listEconomicReportGeoJson() {
  return axios.get(ECONOMIC_REPORT_GEOJSON_PATH).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function newEconomicReport(data) {
  return axios.post(NEW_ECONOMIC_REPORTS_PATH, data).then((res) => {
    if (res.status === 202) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function getEconomicReport(id) {
  return axios.get(GET_ECONOMIC_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function updateEconomicReport(id, data) {
  return axios.patch(GET_ECONOMIC_REPORTS_PATH.replace(':id', id), data).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function deleteEconomicReport(id) {
  return axios.delete(GET_ECONOMIC_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200 || res.status === 204) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function inspectEconomicReportUpload(formData) {
  return axios.post(INSPECT_ECONOMIC_REPORT_UPLOAD_PATH, formData).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function downloadEconomicReportArtifact(downloadPath) {
  const safePath = normalizeEconomicReportArtifactPath(downloadPath)

  if (!safePath) {
    return Promise.resolve({
      ok: false,
      error_type: 'invalid_download_path',
    })
  }

  return axios
    .get(safePath, {
      responseType: 'blob',
      params: {_download: Date.now()},
      headers: {'Cache-Control': 'no-cache'},
    })
    .then((res) => {
      if (res.status === 200) {
        return {
          ok: true,
          data: res.data,
          headers: res.headers,
        }
      }
    })
}

const normalizeTechnicalReportList = (data = {}) => ({
  ...data,
  technical_reports: data.technical_reports || data.technicalReports || [],
})

export function listTechnicalReports(page, size) {
  return axios
    .get(LIST_TECHNICAL_REPORTS_PATH, {
      params: {page, size},
    })
    .then((res) => {
      if (res.status === 200) {
        return {
          ok: true,
          data: normalizeTechnicalReportList(res.data),
        }
      }
    })
}

export function saveTechnicalReport(data) {
  return axios.post(NEW_TECHNICAL_REPORTS_PATH, data).then((res) => {
    if ([200, 201, 202].includes(res.status)) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function getTechnicalReport(id) {
  return axios.get(GET_TECHNICAL_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function deleteTechnicalReport(id) {
  return axios.delete(GET_TECHNICAL_REPORTS_PATH.replace(':id', id)).then((res) => {
    if (res.status === 200 || res.status === 204) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}

export function uploadTechnicalReportPdf(id, blob, filename) {
  const data = new FormData()
  data.append('pdf', blob, filename || 'reporte-tecnico.pdf')

  return axios
    .post(
      TECHNICAL_REPORT_ARTIFACT_PATH.replace(':id', id).replace(':artifact', 'pdf'),
      data
    )
    .then((res) => {
      if ([200, 201, 202].includes(res.status)) {
        return {
          ok: true,
          data: res.data,
        }
      }
    })
}

export function downloadTechnicalReportArtifact(downloadPath) {
  const safePath = normalizeTechnicalReportArtifactPath(downloadPath)

  if (!safePath) {
    return Promise.resolve({
      ok: false,
      error_type: 'invalid_download_path',
    })
  }

  return axios.get(safePath, {responseType: 'blob'}).then((res) => {
    if (res.status === 200) {
      return {
        ok: true,
        data: res.data,
      }
    }
  })
}
